import { useCallback, useEffect, useMemo, useState } from "react";
import { offices as officesService } from "../api/offices.service";
import * as api from "../api/client";

export type JurisdictionOption = {
  value: string;
  label: string;
};

type RegionOption = {
  id: string;
  name: string;
  icon: string | null;
};

const HIDDEN_COUNTRY_IDS = new Set(["1151955"]);

export const GLOBAL_REGION_ID = "GLOBAL";
export const GLOBAL_REGION_LABEL = "Global (all regions)";

export function withGlobalRegionFirst(options: JurisdictionOption[]): JurisdictionOption[] {
  const global = options.filter(option => option.value === GLOBAL_REGION_ID);
  return [...global, ...options.filter(option => option.value !== GLOBAL_REGION_ID)];
}

export type JurisdictionType = "region" | "country";

export function useJurisdictionOptions() {
  const [regionOptions, setRegionOptions] = useState<JurisdictionOption[]>([]);
  const [countryOptions, setCountryOptions] = useState<JurisdictionOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function fetchOptions() {
      setLoading(true);
      setError(null);
      try {
        const [countries, regionsResponse] = await Promise.all([
          officesService.getCountries(),
          api.get<{ regions: RegionOption[] }>("/v1/fulfilment/subscription-regions"),
        ]);

        const regionOpts: JurisdictionOption[] = regionsResponse.regions.map(r => {
          const name = r.id === GLOBAL_REGION_ID ? GLOBAL_REGION_LABEL : r.name;
          return { value: r.id, label: r.icon ? `${r.icon} ${name}` : name };
        });
        regionOpts.sort((a, b) => a.label.localeCompare(b.label));

        const regionValues = new Set(regionOpts.map(r => r.value));
        const countryOpts = countries
          .filter(
            country =>
              !regionValues.has(String(country.id)) && !HIDDEN_COUNTRY_IDS.has(String(country.id)),
          )
          .map(country => ({
            value: String(country.id),
            label: country.name,
          }));
        countryOpts.sort((a, b) => a.label.localeCompare(b.label));

        setRegionOptions(regionOpts);
        setCountryOptions(countryOpts);
      } catch (err) {
        setError(err instanceof Error ? err : new Error("Failed to fetch jurisdiction options"));
      } finally {
        setLoading(false);
      }
    }

    fetchOptions();
  }, []);

  const allOptions = useMemo(() => {
    const combined = [...regionOptions, ...countryOptions];
    combined.sort((a, b) => a.label.localeCompare(b.label));
    return combined;
  }, [regionOptions, countryOptions]);

  const regionValueSet = useMemo(() => new Set(regionOptions.map(r => r.value)), [regionOptions]);

  const detectLocationType = useCallback(
    (jurisdictionId: string): JurisdictionType => {
      return regionValueSet.has(jurisdictionId) ? "region" : "country";
    },
    [regionValueSet],
  );

  return { regionOptions, countryOptions, allOptions, loading, error, detectLocationType };
}
